/**
 * Rich Text Editor Component
 * Wraps react-quill with custom styling for dark theme
 * Note: react-quill 2.0.0 uses findDOMNode internally which triggers warnings in React 18 Strict Mode
 * This is a known issue and will be fixed in react-quill 3.0
 */

import React, { useRef, useMemo, useEffect, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './RichTextEditor.css';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  className?: string;
  rows?: number;
  onImageUpload?: (file: File) => Promise<string>;
  onImageResolved?: (previewUrl: string, finalUrl: string) => void;
  onUploadingChange?: (uploading: boolean) => void;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter text...',
  readOnly = false,
  className = '',
  rows: _rows,
  onImageUpload,
  onImageResolved,
  onUploadingChange,
}) => {
  const quillRef = useRef<ReactQuill>(null);

  useEffect(() => {
    const originalError = console.error;
    console.error = (...args: any[]) => {
      if (
        typeof args[0] === 'string' &&
        args[0].includes('findDOMNode')
      ) {
        return;
      }
      originalError.apply(console, args);
    };

    return () => {
      console.error = originalError;
    };
  }, []);

  const readAsDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const insertImage = useCallback(async (file: File) => {
    if (!onImageUpload) return;
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const range = quill.getSelection(true) ?? { index: quill.getLength(), length: 0 };
    const previewUrl = await readAsDataUrl(file);
    quill.insertEmbed(range.index, 'image', previewUrl, 'user');
    quill.setSelection(range.index + 1, 0);
    onUploadingChange?.(true);
    try {
      const finalUrl = await onImageUpload(file);
      onImageResolved?.(previewUrl, finalUrl);
    } catch {
    } finally {
      onUploadingChange?.(false);
    }
  }, [onImageUpload, onImageResolved, onUploadingChange]);

  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) void insertImage(file);
    };
    input.click();
  }, [insertImage]);

  useEffect(() => {
    if (!onImageUpload) return;
    const root = quillRef.current?.getEditor()?.root;
    if (!root) return;
    const handlePaste = (event: ClipboardEvent) => {
      const item = Array.from(event.clipboardData?.items || []).find((i) => i.type.startsWith('image/'));
      const file = item?.getAsFile();
      if (!file) return;
      event.preventDefault();
      void insertImage(file);
    };
    root.addEventListener('paste', handlePaste);
    return () => root.removeEventListener('paste', handlePaste);
  }, [onImageUpload, insertImage]);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ align: [] }],
        onImageUpload ? ['link', 'image'] : ['link'],
        ['clean'],
      ],
      handlers: onImageUpload ? { image: imageHandler } : {},
    },
  }), [onImageUpload, imageHandler]);

  const formats = useMemo(() => [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'indent',
    'align',
    'link',
    ...(onImageUpload ? ['image'] : []),
  ], [onImageUpload]);

  return (
    <div className={`rich-text-editor-wrapper ${className} pb-5`}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        readOnly={readOnly}
      />
    </div>
  );
};

export default RichTextEditor;

